import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, MATERIALS, MATERIAL_COLORS, BENNE_SIZES_ALL, allowedBenneSizes, formatApiError, UNIT_PRICED_MATERIALS, UNIT_LABELS } from "@/lib/apiClient";
import { Coins, Share2, MessageCircle, Mail, FileDown, Link2, Check, Save, ChevronDown, Copy } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BENNE_SIZES = BENNE_SIZES_ALL;

export default function PricingGrid({ user }) {
  const [grid, setGrid] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [copied, setCopied] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [textCopied, setTextCopied] = useState(false);
  const [emailMenuOpen, setEmailMenuOpen] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const emailWrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (emailWrapRef.current && !emailWrapRef.current.contains(e.target)) {
        setEmailMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pricing");
        setGrid(data);
      } catch (e) {
        setError(formatApiError(e?.response?.data?.detail) || e.message);
      }
    })();
  }, []);

  const setPerTon = (mat, value) => {
    setGrid((g) => ({
      ...g,
      prices: {
        ...g.prices,
        [mat]: { ...g.prices[mat], per_ton: value },
      },
    }));
    setDirty(true);
    setSavedOk(false);
  };

  const setPerBenne = (mat, size, value) => {
    setGrid((g) => ({
      ...g,
      prices: {
        ...g.prices,
        [mat]: {
          ...g.prices[mat],
          per_benne: { ...g.prices[mat].per_benne, [size]: value },
        },
      },
    }));
    setDirty(true);
    setSavedOk(false);
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const cleaned = {};
      for (const m of MATERIALS) {
        const row = grid.prices[m] || {};
        cleaned[m] = {
          per_ton: parseFloat(row.per_ton) || 0,
          per_benne: Object.fromEntries(
            allowedBenneSizes(m).map((s) => [s, parseFloat(row.per_benne?.[s]) || 0]),
          ),
        };
      }
      const { data } = await api.put("/pricing", { prices: cleaned });
      setGrid(data);
      setDirty(false);
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 2500);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  // ----- Share helpers -----
  const publicUrl = useMemo(() => {
    if (!grid?.share_token) return "";
    return `${window.location.origin}/tarifs/${grid.share_token}`;
  }, [grid?.share_token]);

  const buildPlainText = useMemo(() => {
    return () => {
      if (!grid) return "";
      const lines = [];
      lines.push("🌱 FB ENVIRONNEMENT — GRILLE TARIFAIRE");
      lines.push("");
      lines.push(`Commercial : ${user?.name || ""}`);
      if (user?.email) lines.push(`Contact : ${user.email}`);
      lines.push("━━━━━━━━━━━━━━━━━━━━");
      let hasAny = false;
      for (const m of MATERIALS) {
        const row = grid.prices[m];
        if (!row) continue;
        const ton = parseFloat(row.per_ton) || 0;
        const bennes = allowedBenneSizes(m).filter((s) => (parseFloat(row.per_benne?.[s]) || 0) > 0);
        if (ton === 0 && bennes.length === 0) continue;
        hasAny = true;
        lines.push("");
        lines.push(`*${m}*`);
        if (ton > 0) lines.push(`  • À la tonne : ${ton} €/t`);
        for (const s of bennes) {
          lines.push(`  • Benne ${s} m³ : ${row.per_benne[s]} €`);
        }
      }
      if (!hasAny) {
        lines.push("");
        lines.push("(Aucun tarif renseigné)");
      }
      lines.push("");
      lines.push("━━━━━━━━━━━━━━━━━━━━");
      lines.push("Tarifs en ligne :");
      lines.push(publicUrl);
      return lines.join("\n");
    };
  }, [grid, user, publicUrl]);

  const whatsappHref = useMemo(
    () => `https://wa.me/?text=${encodeURIComponent(buildPlainText())}`,
    [buildPlainText],
  );

  const emailHref = useMemo(
    () =>
      `mailto:?subject=${encodeURIComponent(
        "Grille tarifaire — FB Environnement",
      )}&body=${encodeURIComponent(buildPlainText())}`,
    [buildPlainText],
  );

  const gmailHref = useMemo(
    () =>
      `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(
        "Grille tarifaire — FB Environnement",
      )}&body=${encodeURIComponent(buildPlainText())}`,
    [buildPlainText],
  );

  const outlookHref = useMemo(
    () =>
      `https://outlook.live.com/mail/0/deeplink/compose?subject=${encodeURIComponent(
        "Grille tarifaire — FB Environnement",
      )}&body=${encodeURIComponent(buildPlainText())}`,
    [buildPlainText],
  );

  const copyEmailContent = async () => {
    const fullText = `Objet : Grille tarifaire — FB Environnement\n\n${buildPlainText()}`;
    try {
      await navigator.clipboard.writeText(fullText);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = fullText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
    } catch {
      const input = document.createElement("input");
      input.value = publicUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(buildPlainText());
    } catch {
      const ta = document.createElement("textarea");
      ta.value = buildPlainText();
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setTextCopied(true);
    setTimeout(() => setTextCopied(false), 2000);
  };

  const exportPDF = () => {
    if (!grid) return;
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

    // Header
    doc.setFillColor(16, 185, 129);
    doc.rect(0, 0, 297, 22, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("FB ENVIRONNEMENT", 14, 11);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Grille tarifaire — Tarifs en € HT", 14, 17);

    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);
    doc.text(`Commercial : ${user?.name || ""}`, 200, 11);
    if (user?.email) doc.text(user.email, 200, 17);

    const head = [["Matière", "€ HT/tonne", ...BENNE_SIZES.map((s) => `${s} m³`)]];
    const body = MATERIALS.map((m) => {
      const row = grid.prices[m] || {};
      const ton = parseFloat(row.per_ton) || 0;
      const allowed = allowedBenneSizes(m);
      return [
        m,
        ton > 0 ? `${ton} €` : "—",
        ...BENNE_SIZES.map((s) => {
          if (!allowed.includes(s)) return "—"; // taille non autorisée pour cette matière
          const v = parseFloat(row.per_benne?.[s]) || 0;
          return v > 0 ? `${v} €` : "—";
        }),
      ];
    });

    autoTable(doc, {
      head,
      body,
      startY: 28,
      styles: { fontSize: 9, halign: "center" },
      headStyles: { fillColor: [28, 25, 23], textColor: 255, fontStyle: "bold" },
      columnStyles: { 0: { halign: "left", fontStyle: "bold", cellWidth: 38 } },
      alternateRowStyles: { fillColor: [250, 250, 249] },
    });

    const finalY = doc.lastAutoTable?.finalY || 40;
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(`Tarifs en ligne : ${publicUrl}`, 14, finalY + 10);
    doc.text(
      `Édité le ${new Date().toLocaleDateString("fr-FR")} — Tarifs en € HT, indicatifs, valables sous réserve d'acceptation.`,
      14,
      finalY + 15,
    );

    doc.save(`Tarifs-FB-Environnement-${user?.name?.replace(/\s+/g, "-") || "commercial"}.pdf`);
  };

  if (!grid) {
    return (
      <div className="bg-white rounded-3xl border border-stone-100 p-6 text-center text-stone-400">
        Chargement de la grille tarifaire…
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl border border-stone-100 p-5 sm:p-6 shadow-[0_4px_20px_rgb(0,0,0,0.04)]" data-testid="pricing-grid">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-emerald-500 text-white grid place-items-center shadow-lg shadow-emerald-500/30">
            <Coins className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <h3 className="font-heading font-black text-lg leading-tight">Ma grille tarifaire</h3>
            <p className="text-xs text-stone-500">
              <span className="inline-flex items-center gap-1 mr-2 bg-stone-900 text-white font-black tracking-wider px-1.5 py-0.5 rounded text-[10px]">€ HT</span>
              Tous les prix sont hors taxes
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dirty && (
            <span className="text-xs font-bold text-amber-600">Non enregistré</span>
          )}
          {savedOk && !dirty && (
            <span className="text-xs font-bold text-emerald-600 inline-flex items-center gap-1">
              <Check className="w-3.5 h-3.5" strokeWidth={3} /> Enregistré
            </span>
          )}
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            data-testid="pricing-save"
            className="inline-flex items-center gap-1.5 bg-stone-900 hover:bg-stone-800 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold px-3 py-2 rounded-full text-sm transition-all active:scale-95"
          >
            <Save className="w-4 h-4" strokeWidth={2.8} />
            {saving ? "..." : "Enregistrer"}
          </button>
        </div>
      </div>

      {error && (
        <div className="text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-2xl px-4 py-3 mb-3">
          {error}
        </div>
      )}

      {/* Cartes par matière */}
      <div className="space-y-3">
        {MATERIALS.map((m) => {
          const row = grid.prices[m] || {};
          return (
            <div
              key={m}
              data-testid={`material-card-${m}`}
              className="rounded-2xl border border-stone-100 bg-stone-50/60 hover:bg-white transition-colors p-4"
            >
              {/* Header : matière + prix tonne (ou prix à la pièce pour les matières unitaires) */}
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <span
                  className={`px-3 py-1.5 rounded-full text-sm font-black whitespace-nowrap ${MATERIAL_COLORS[m] || "bg-stone-500 text-white"}`}
                >
                  {m}
                </span>
                <div className="flex items-center gap-1.5 shrink-0">
                  {UNIT_PRICED_MATERIALS.includes(m) ? (
                    <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full uppercase tracking-wide whitespace-nowrap">
                      €/{UNIT_LABELS[m] || "unité"}
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold text-stone-500 uppercase tracking-wide whitespace-nowrap">€/tonne</span>
                  )}
                  <div className="relative">
                    <input
                      data-testid={`price-ton-${m}`}
                      type="number"
                      step="0.01"
                      min="0"
                      value={row.per_ton === 0 || row.per_ton == null ? "" : row.per_ton}
                      onChange={(e) => setPerTon(m, e.target.value)}
                      placeholder="0"
                      className="w-24 text-right pr-10 bg-white border-2 border-stone-200 focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 outline-none rounded-xl px-2 py-2 text-base font-black tabular-nums transition-all"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 font-bold text-[11px] pointer-events-none whitespace-nowrap">
                      € <span className="text-[9px] font-black text-emerald-600 tracking-wide">HT</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Bennes en grille — masquée si aucune taille autorisée (ex : Pneus VL/PL) */}
              {allowedBenneSizes(m).length > 0 && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-stone-500 mb-2">
                    Par benne (m³)
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-1.5">
                    {allowedBenneSizes(m).map((s) => (
                      <BenneCell
                        key={s}
                        size={s}
                        value={row.per_benne?.[s]}
                        onChange={(v) => setPerBenne(m, s, v)}
                        testid={`price-benne-${m}-${s}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Share row */}
      <div className="mt-5 pt-5 border-t border-stone-100">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Share2 className="w-4 h-4 text-stone-600" strokeWidth={2.5} />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">
              Envoyer à un client
            </span>
          </div>
          {dirty && (
            <span className="text-[11px] font-bold text-amber-600">
              {"Pense à enregistrer d'abord !"}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-whatsapp"
            className="flex items-center justify-center gap-2 font-bold px-4 py-3 rounded-full transition-all active:scale-95 shadow-lg bg-[#25D366] hover:bg-[#1ebe5b] text-white shadow-[#25D366]/30"
          >
            <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
            <span className="text-sm">WhatsApp</span>
          </a>
          <div className="relative" ref={emailWrapRef}>
            <button
              type="button"
              onClick={() => setEmailMenuOpen((o) => !o)}
              data-testid="share-email"
              className="w-full flex items-center justify-center gap-2 font-bold px-4 py-3 rounded-full transition-all active:scale-95 shadow-lg bg-sky-500 hover:bg-sky-600 text-white shadow-sky-500/30"
            >
              <Mail className="w-5 h-5" strokeWidth={2.5} />
              <span className="text-sm">Email</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${emailMenuOpen ? "rotate-180" : ""}`} strokeWidth={3} />
            </button>
            {emailMenuOpen && (
              <div
                data-testid="share-email-menu"
                className="absolute z-30 top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-white border border-stone-200 rounded-2xl shadow-2xl overflow-hidden"
              >
                <a
                  href={gmailHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setEmailMenuOpen(false)}
                  data-testid="share-email-gmail"
                  className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-stone-800 hover:bg-stone-50 transition-colors"
                >
                  <span className="w-7 h-7 rounded-full bg-red-50 text-red-600 grid place-items-center text-base font-black">G</span>
                  Gmail (web)
                </a>
                <a
                  href={outlookHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setEmailMenuOpen(false)}
                  data-testid="share-email-outlook"
                  className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-stone-800 hover:bg-stone-50 transition-colors border-t border-stone-100"
                >
                  <span className="w-7 h-7 rounded-full bg-sky-50 text-sky-700 grid place-items-center text-base font-black">O</span>
                  Outlook (web)
                </a>
                <a
                  href={emailHref}
                  onClick={() => setEmailMenuOpen(false)}
                  data-testid="share-email-mailto"
                  className="flex items-center gap-3 px-4 py-3 text-sm font-bold text-stone-800 hover:bg-stone-50 transition-colors border-t border-stone-100"
                >
                  <Mail className="w-5 h-5 text-stone-500" strokeWidth={2.5} />
                  Mail de l&apos;appareil
                </a>
                <button
                  type="button"
                  onClick={() => {
                    copyEmailContent();
                    setEmailMenuOpen(false);
                  }}
                  data-testid="share-email-copy"
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-stone-800 hover:bg-stone-50 transition-colors border-t border-stone-100"
                >
                  {emailCopied ? (
                    <Check className="w-5 h-5 text-emerald-600" strokeWidth={3} />
                  ) : (
                    <Copy className="w-5 h-5 text-stone-500" strokeWidth={2.5} />
                  )}
                  {emailCopied ? "Copié !" : "Copier le message"}
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={exportPDF}
            data-testid="share-pdf"
            className="flex items-center justify-center gap-2 font-bold px-4 py-3 rounded-full transition-all active:scale-95 shadow-lg bg-rose-500 hover:bg-rose-600 text-white shadow-rose-500/30"
          >
            <FileDown className="w-5 h-5" strokeWidth={2.5} />
            <span className="text-sm">PDF</span>
          </button>
          <button
            type="button"
            onClick={copyLink}
            data-testid="share-link"
            className="flex items-center justify-center gap-2 font-bold px-4 py-3 rounded-full transition-all active:scale-95 shadow-lg bg-stone-900 hover:bg-stone-800 text-white shadow-stone-900/20"
          >
            {copied ? <Check className="w-5 h-5" strokeWidth={3} /> : <Link2 className="w-5 h-5" strokeWidth={2.5} />}
            <span className="text-sm">{copied ? "Copié !" : "Copier le lien"}</span>
          </button>
        </div>

        {/* Aperçu pliable du texte envoyé */}
        <button
          type="button"
          onClick={() => setShowPreview((v) => !v)}
          data-testid="preview-toggle"
          className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-stone-500 hover:text-stone-900 transition-colors"
        >
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform ${showPreview ? "rotate-180" : ""}`}
            strokeWidth={3}
          />
          {showPreview ? "Masquer" : "Voir / copier le texte envoyé"}
        </button>
        {showPreview && (
          <div className="mt-2 bg-stone-50 border border-stone-200 rounded-2xl p-3">
            <pre
              data-testid="share-preview"
              className="whitespace-pre-wrap text-xs text-stone-700 font-mono leading-relaxed max-h-64 overflow-y-auto"
            >
              {buildPlainText()}
            </pre>
            <button
              type="button"
              onClick={copyText}
              data-testid="copy-text"
              className="mt-2 inline-flex items-center gap-1.5 bg-stone-900 hover:bg-stone-800 text-white font-bold px-3 py-1.5 rounded-full text-xs active:scale-95"
            >
              {textCopied ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : <Link2 className="w-3.5 h-3.5" strokeWidth={2.5} />}
              {textCopied ? "Texte copié !" : "Copier ce texte"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function BenneCell({ size, value, onChange, testid }) {
  const display = value === 0 || value === "0" || value == null ? "" : value;
  const filled = display !== "";
  return (
    <label
      style={filled ? { borderColor: "var(--brand-primary)", boxShadow: "0 1px 2px 0 color-mix(in srgb, var(--brand-primary) 20%, transparent)" } : undefined}
      className={`flex flex-col items-center rounded-xl border-2 transition-all p-1.5 cursor-text bg-white ${
        filled ? "" : "border-stone-200 hover:border-stone-300"
      } focus-within:!border-[var(--brand-primary)] focus-within:ring-2 focus-within:ring-[color-mix(in_srgb,var(--brand-primary)_20%,transparent)]`}
    >
      <span
        style={filled ? { color: "var(--brand-primary)" } : undefined}
        className={`text-[10px] font-black tracking-wider mb-0.5 ${filled ? "" : "text-stone-400"}`}
      >
        {size} m³
      </span>
      <div className="relative w-full flex items-center">
        <input
          data-testid={testid}
          type="number"
          step="0.01"
          min="0"
          value={display}
          onChange={(e) => onChange(e.target.value)}
          placeholder="—"
          className="w-full text-center bg-transparent outline-none text-sm font-bold tabular-nums placeholder:text-stone-300"
        />
        {filled && (
          <span
            style={{ color: "var(--brand-primary)" }}
            className="absolute right-0 top-1/2 -translate-y-1/2 text-[8px] font-black tracking-wider pointer-events-none"
          >
            HT
          </span>
        )}
      </div>
    </label>
  );
}

function ShareButton({ icon, label, onClick, testid, className = "" }) {
  // Conservé pour compatibilité éventuelle
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center justify-center gap-2 font-bold px-4 py-3 rounded-full transition-all active:scale-95 shadow-lg ${className}`}
    >
      {icon}
      <span className="text-sm">{label}</span>
    </button>
  );
}
